import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { Trash2, Clock } from 'lucide-react';
import { format, isSameDay, isWeekend } from 'date-fns';
import * as S from '../../Common/internalToolsStyles';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { CATEGORY_OPTIONS, CATEGORY_LABELS } from './timeConstants';

const CAL_START_HOUR = 6;     // 06:00 top of scroll
const CAL_END_HOUR = 20;      // 20:00 bottom
const HOUR_HEIGHT = 48;       // px per hour — 12px per 15-min slot
const SNAP_MINUTES = 15;
const MIN_BLOCK_MINUTES = 15;

const minutesFromMidnight = (dateStr) => {
  if (!dateStr) return null;
  // Accept ISO datetimes or MySQL-formatted "YYYY-MM-DD HH:MM:SS"
  const d = new Date(typeof dateStr === 'string' && dateStr.includes(' ') ? dateStr.replace(' ', 'T') : dateStr);
  if (isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
};

const fmtClock = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Toggl-style day total: H:MM:SS (we track minutes only so seconds are :00).
const fmtDayTotal = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}:00`;
};

const snap = (minutes, step = SNAP_MINUTES) => Math.round(minutes / step) * step;

// Build `YYYY-MM-DDTHH:MM:00` in the browser's local time. Backend stores
// datetime without TZ conversion, so local-time ISO-ish strings are what we
// send and render consistently.
const buildLocalDatetime = (day, minutes) => {
  const date = new Date(day);
  date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${d}T${h}:${mi}:00`;
};

// Turn a weekEntries array into two buckets:
// - positioned: work entries with a start_time (placed on the time grid)
// - allDay: absences + work entries without start_time (chips at the top)
const bucketEntries = (weekEntries, days) => {
  const byDay = days.map(() => ({ positioned: [], allDay: [] }));
  for (const e of weekEntries) {
    const dayStr = format(new Date(e.entry_date), 'yyyy-MM-dd');
    const di = days.findIndex(d => format(d, 'yyyy-MM-dd') === dayStr);
    if (di < 0) continue;
    const startMins = e.kind === 'work' ? minutesFromMidnight(e.start_time) : null;
    if (startMins !== null) {
      byDay[di].positioned.push({ ...e, _startMins: startMins, _durationMins: e.duration_minutes || 0 });
    } else {
      byDay[di].allDay.push(e);
    }
  }
  // Within each day, sort positioned by start time
  for (const bucket of byDay) {
    bucket.positioned.sort((a, b) => a._startMins - b._startMins);
  }
  return byDay;
};

// Simple side-by-side layout for overlapping blocks — each block takes a
// fractional column width based on how many other blocks share its slot.
// Not the Google Calendar packing algorithm, but decent for the common case
// of sparse overlaps.
const layoutOverlaps = (positioned) => {
  // Assign each block a "column" index among its overlap cluster.
  const sorted = [...positioned].sort((a, b) => a._startMins - b._startMins);
  const active = []; // [{block, col}]
  const clusters = [];
  let cluster = null;
  for (const block of sorted) {
    const start = block._startMins;
    const end = block._startMins + block._durationMins;
    // Drop non-overlapping active blocks
    for (let i = active.length - 1; i >= 0; i--) {
      if (active[i].block._startMins + active[i].block._durationMins <= start) active.splice(i, 1);
    }
    if (active.length === 0) {
      if (cluster) clusters.push(cluster);
      cluster = { blocks: [], maxCols: 0 };
    }
    // Assign the lowest free column
    const takenCols = new Set(active.map(a => a.col));
    let col = 0;
    while (takenCols.has(col)) col++;
    active.push({ block, col });
    cluster.blocks.push({ block, col });
    cluster.maxCols = Math.max(cluster.maxCols, col + 1);
    // Overlap with any still-active block is implicit
    block._end = end;
  }
  if (cluster && cluster.blocks.length) clusters.push(cluster);
  // Flatten with width + left offset
  const result = [];
  for (const c of clusters) {
    for (const { block, col } of c.blocks) {
      result.push({
        ...block,
        _leftPct: (col / c.maxCols) * 100,
        _widthPct: (1 / c.maxCols) * 100,
      });
    }
  }
  return result;
};

const WeekCalendarGrid = ({
  days,
  today,
  weekEntries,
  projects,
  leaveTypes,
  isReadOnly,
  companyCurrency = 'SEK',
  onCreateEntry,
  onUpdateEntry,
  onDeleteEntry,
}) => {
  const weekdayNames = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
  const hours = useMemo(() => {
    const arr = [];
    for (let h = CAL_START_HOUR; h <= CAL_END_HOUR; h++) arr.push(h);
    return arr;
  }, []);
  const bodyHeight = (CAL_END_HOUR - CAL_START_HOUR) * HOUR_HEIGHT;

  // Drag state — a single shared state machine covers create/move/resize.
  // `kind` tells the pointermove handler which math to run.
  const [drag, setDrag] = useState(null);
  // { kind: 'create' | 'move' | 'resize', dayIdx, entryId?, startMins, durationMins, originalStartMins?, originalDuration? }
  const dragRef = useRef(null);
  useEffect(() => { dragRef.current = drag; }, [drag]);

  // Modal state — create (click empty slot or drag-to-create release) + edit.
  const [createModal, setCreateModal] = useState(null);
  // { day, startMins, durationMins }
  const [editModal, setEditModal] = useState(null);
  // { entry }

  // Now-line position (today only) — ticks every minute.
  const [nowMinutes, setNowMinutes] = useState(() => {
    const n = new Date();
    return n.getHours() * 60 + n.getMinutes();
  });
  useEffect(() => {
    const t = setInterval(() => {
      const n = new Date();
      setNowMinutes(n.getHours() * 60 + n.getMinutes());
    }, 60000);
    return () => clearInterval(t);
  }, []);

  const buckets = useMemo(() => bucketEntries(weekEntries, days), [weekEntries, days]);
  const hasAnyAllDay = buckets.some(b => b.allDay.length > 0);

  const colRefs = useRef([]);

  // ─── Pointer helpers ───────────────────────────────────────────────────
  const minutesFromPointer = useCallback((dayIdx, clientY) => {
    const col = colRefs.current[dayIdx];
    if (!col) return 0;
    const rect = col.getBoundingClientRect();
    const y = clientY - rect.top;
    const raw = CAL_START_HOUR * 60 + (y / HOUR_HEIGHT) * 60;
    const clamped = Math.max(CAL_START_HOUR * 60, Math.min((CAL_END_HOUR + 1) * 60 - SNAP_MINUTES, raw));
    return snap(clamped);
  }, []);

  // ─── Drag handlers ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!drag) return;
    const handleMove = (e) => {
      const cur = dragRef.current;
      if (!cur) return;
      const pt = e.touches ? e.touches[0] : e;
      const mins = minutesFromPointer(cur.dayIdx, pt.clientY);
      if (cur.kind === 'create') {
        const start = Math.min(cur.originalStartMins, mins);
        const end = Math.max(cur.originalStartMins + SNAP_MINUTES, mins);
        setDrag(d => d && ({ ...d, startMins: start, durationMins: Math.max(MIN_BLOCK_MINUTES, end - start) }));
      } else if (cur.kind === 'move') {
        const delta = mins - cur.pointerOriginMins;
        const newStart = Math.max(CAL_START_HOUR * 60,
          Math.min((CAL_END_HOUR + 1) * 60 - cur.durationMins, cur.originalStartMins + delta));
        setDrag(d => d && ({ ...d, startMins: snap(newStart) }));
      } else if (cur.kind === 'resize') {
        const newDuration = Math.max(MIN_BLOCK_MINUTES,
          Math.min((CAL_END_HOUR + 1) * 60 - cur.startMins, mins - cur.startMins));
        setDrag(d => d && ({ ...d, durationMins: snap(newDuration) }));
      }
    };

    const handleUp = async () => {
      const cur = dragRef.current;
      if (!cur) return;
      setDrag(null);
      if (cur.kind === 'create') {
        // Open create modal prefilled with the dragged range.
        setCreateModal({
          day: days[cur.dayIdx],
          startMins: cur.startMins,
          durationMins: cur.durationMins,
        });
      } else if (cur.kind === 'move' && cur.entryId) {
        // Only fire if the block actually moved.
        if (cur.startMins !== cur.originalStartMins || cur.dayIdx !== cur.originalDayIdx) {
          const newDay = days[cur.dayIdx];
          await onUpdateEntry?.(cur.entryId, {
            entryDate: format(newDay, 'yyyy-MM-dd'),
            startTime: buildLocalDatetime(newDay, cur.startMins),
            endTime: buildLocalDatetime(newDay, cur.startMins + cur.durationMins),
            durationMinutes: cur.durationMins,
          });
        }
      } else if (cur.kind === 'resize' && cur.entryId) {
        if (cur.durationMins !== cur.originalDuration) {
          const day = days[cur.dayIdx];
          await onUpdateEntry?.(cur.entryId, {
            startTime: buildLocalDatetime(day, cur.startMins),
            endTime: buildLocalDatetime(day, cur.startMins + cur.durationMins),
            durationMinutes: cur.durationMins,
          });
        }
      }
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [drag, days, minutesFromPointer, onUpdateEntry]);

  // Click/drag on empty column area → create
  const handleColPointerDown = (e, dayIdx) => {
    if (isReadOnly) return;
    // Only primary button / touch. Ignore if pointer is on a block.
    if (e.target.closest('.tr-cal-block')) return;
    if (e.button !== undefined && e.button !== 0) return;
    const mins = minutesFromPointer(dayIdx, e.clientY);
    setDrag({
      kind: 'create',
      dayIdx,
      originalDayIdx: dayIdx,
      originalStartMins: mins,
      startMins: mins,
      durationMins: SNAP_MINUTES,
    });
  };

  const handleBlockPointerDown = (e, block, dayIdx, mode) => {
    if (isReadOnly) return;
    e.stopPropagation();
    const pointerMins = minutesFromPointer(dayIdx, e.clientY);
    setDrag({
      kind: mode, // 'move' | 'resize'
      entryId: block.entry_id,
      dayIdx,
      originalDayIdx: dayIdx,
      originalStartMins: block._startMins,
      originalDuration: block._durationMins,
      startMins: block._startMins,
      durationMins: block._durationMins,
      pointerOriginMins: pointerMins,
    });
  };

  // Click a block (no drag) → edit
  const handleBlockClick = (block) => {
    if (isReadOnly) return;
    const cur = dragRef.current;
    // If we just finished a drag, ignore click (pointerup already handled it).
    if (cur) return;
    setEditModal({ entry: block });
  };

  const dayCount = days.length;
  // Build grid templates dynamically so 5-day weeks fill the full width
  // instead of leaving empty slots where Sat/Sun used to sit.
  const headerCols = `${GUTTER_WIDTH}px repeat(${dayCount}, 1fr)`;
  const bodyCols = `repeat(${dayCount}, 1fr)`;
  // Keep the calendar from collapsing on narrow viewports; scale with count.
  const minWidth = GUTTER_WIDTH + dayCount * 100;

  return (
    <div className="tr-cal" style={{ overflowX: 'auto' }}>
      <div className="tr-cal-inner" style={{ minWidth }}>
        {/* Header — weekday names + dates + day total */}
        <div className="tr-cal-header" style={{ gridTemplateColumns: headerCols }}>
          <div className="tr-cal-gutter-col" />
          {days.map((day, i) => {
            const isToday = isSameDay(day, today);
            const isOff = isWeekend(day);
            const dayStr = format(day, 'yyyy-MM-dd');
            const dayMins = weekEntries.reduce((s, e) => {
              const eDay = format(new Date(e.entry_date), 'yyyy-MM-dd');
              return eDay === dayStr ? s + (e.duration_minutes || 0) : s;
            }, 0);
            const dayHours = dayMins / 60;
            return (
              <div key={i} className={`tr-cal-header-cell ${isToday ? 'today' : ''} ${isOff ? 'weekend' : ''}`}>
                <div className={`tr-cal-date ${isToday ? 'today-date' : ''}`}>{format(day, 'd')}</div>
                <div className="tr-cal-meta">
                  <div className="tr-cal-wd">{weekdayNames[i]}</div>
                  <div className={`tr-cal-day-total ${dayHours > 8 ? 'over' : ''} ${dayHours === 0 ? 'zero' : ''}`}>
                    {fmtDayTotal(dayMins)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* All-day row — only shown if any day has all-day entries */}
        {hasAnyAllDay && (
          <div className="tr-cal-allday" style={{ gridTemplateColumns: headerCols }}>
            <div className="tr-cal-gutter-col tr-cal-allday-label">Hela dagen</div>
            {days.map((day, i) => {
              const isToday = isSameDay(day, today);
              const isOff = isWeekend(day);
              return (
                <div key={i} className={`tr-cal-allday-cell ${isToday ? 'today' : ''} ${isOff ? 'weekend' : ''}`}>
                  {buckets[i].allDay.map(e => (
                    <div
                      key={e.entry_id}
                      className="tr-cal-chip"
                      style={{
                        backgroundColor: e.kind === 'absence' ? '#fef3c7' : `${e.project_color || '#6b7280'}22`,
                        color: e.kind === 'absence' ? '#92400e' : (e.project_color || '#374151'),
                        borderLeft: `3px solid ${e.kind === 'absence' ? '#f59e0b' : (e.project_color || '#6b7280')}`,
                      }}
                      onClick={() => setEditModal({ entry: e })}
                      title={e.kind === 'absence' ? e.leave_type_name : e.project_name}
                    >
                      <span className="tr-cal-chip-hours">{Math.round((e.duration_minutes || 0) / 60 * 10) / 10}h</span>
                      <span className="tr-cal-chip-label">{e.kind === 'absence' ? e.leave_type_name : e.project_name}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Body — time axis + 7 day columns */}
        <div className="tr-cal-body" style={{ height: bodyHeight }}>
          <div className="tr-cal-gutter-col tr-cal-time-gutter">
            {/* Skip the first hour label (it would sit on the header's bottom border) */}
            {hours.slice(1, -1).map((h) => (
              <div key={h} className="tr-cal-hour-label" style={{ top: (h - CAL_START_HOUR) * HOUR_HEIGHT }}>
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
          <div className="tr-cal-cols" style={{ height: bodyHeight, gridTemplateColumns: bodyCols }}>
            {days.map((day, dayIdx) => {
              const isToday = isSameDay(day, today);
              const isOff = isWeekend(day);
              const positioned = layoutOverlaps(buckets[dayIdx].positioned);
              const isDragCreatingHere = drag && drag.kind === 'create' && drag.dayIdx === dayIdx;
              const draggingBlock = drag && (drag.kind === 'move' || drag.kind === 'resize') && drag.dayIdx === dayIdx ? drag : null;

              return (
                <div
                  key={dayIdx}
                  ref={el => (colRefs.current[dayIdx] = el)}
                  className={`tr-cal-col ${isToday ? 'today' : ''} ${isOff ? 'weekend' : ''} ${isReadOnly ? 'readonly' : ''}`}
                  onPointerDown={(e) => handleColPointerDown(e, dayIdx)}
                >
                  {/* Hour grid lines */}
                  {hours.slice(0, -1).map((h, i) => (
                    <div key={h} className={`tr-cal-hour-line ${i % 2 === 1 ? 'alt' : ''}`} style={{ top: i * HOUR_HEIGHT }} />
                  ))}

                  {/* Positioned blocks */}
                  {positioned.map(block => {
                    const isBeingDragged = draggingBlock && draggingBlock.entryId === block.entry_id;
                    const startMins = isBeingDragged ? draggingBlock.startMins : block._startMins;
                    const durationMins = isBeingDragged ? draggingBlock.durationMins : block._durationMins;
                    const top = ((startMins - CAL_START_HOUR * 60) / 60) * HOUR_HEIGHT;
                    const height = (durationMins / 60) * HOUR_HEIGHT;
                    const color = block.project_color || S.colors.textMuted;
                    const catMeta = CATEGORY_LABELS[block.work_category] || CATEGORY_LABELS.ordinary;
                    const isNonOrdinary = block.work_category && block.work_category !== 'ordinary';
                    return (
                      <div
                        key={block.entry_id}
                        className={`tr-cal-block ${isBeingDragged ? 'dragging' : ''}`}
                        style={{
                          top,
                          height,
                          left: `calc(${block._leftPct}% + 2px)`,
                          width: `calc(${block._widthPct}% - 4px)`,
                          background: `${color}1f`,
                          color: '#111827',
                        }}
                        onPointerDown={(e) => handleBlockPointerDown(e, block, dayIdx, 'move')}
                        onClick={() => handleBlockClick(block)}
                        title={`${block.project_name || 'Projekt'} · ${fmtClock(startMins)}–${fmtClock(startMins + durationMins)}`}
                      >
                        <div className="tr-cal-block-head">
                          <span className="tr-cal-block-time">
                            {fmtClock(startMins)}–{fmtClock(startMins + durationMins)}
                          </span>
                          {isNonOrdinary && (
                            <span className="tr-cal-block-cat" style={{ background: `${catMeta.color}22`, color: catMeta.color }}>
                              {catMeta.short}
                            </span>
                          )}
                        </div>
                        <div className="tr-cal-block-title">{block.project_name || 'Projekt'}</div>
                        {block.description && height > 46 && (
                          <div className="tr-cal-block-desc">{block.description}</div>
                        )}
                        {!isReadOnly && height >= 30 && (
                          <div
                            className="tr-cal-resize-handle"
                            onPointerDown={(e) => handleBlockPointerDown(e, block, dayIdx, 'resize')}
                          />
                        )}
                      </div>
                    );
                  })}

                  {/* Live drag-create preview */}
                  {isDragCreatingHere && (
                    <div
                      className="tr-cal-creating"
                      style={{
                        top: ((drag.startMins - CAL_START_HOUR * 60) / 60) * HOUR_HEIGHT,
                        height: (drag.durationMins / 60) * HOUR_HEIGHT,
                      }}
                    >
                      <div className="tr-cal-creating-label">
                        {fmtClock(drag.startMins)}–{fmtClock(drag.startMins + drag.durationMins)}
                      </div>
                    </div>
                  )}

                  {/* Now-line (today only, only if within visible range) */}
                  {isToday && nowMinutes >= CAL_START_HOUR * 60 && nowMinutes <= CAL_END_HOUR * 60 && (
                    <div
                      className="tr-cal-now"
                      style={{ top: ((nowMinutes - CAL_START_HOUR * 60) / 60) * HOUR_HEIGHT }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Create modal */}
      <CreateEntryModal
        show={!!createModal}
        slot={createModal}
        projects={projects}
        companyCurrency={companyCurrency}
        onHide={() => setCreateModal(null)}
        onSubmit={async (payload) => {
          await onCreateEntry?.(payload);
          setCreateModal(null);
        }}
      />

      {/* Edit modal */}
      <EditEntryModal
        show={!!editModal}
        entry={editModal?.entry}
        projects={projects}
        leaveTypes={leaveTypes}
        onHide={() => setEditModal(null)}
        onSubmit={async (entryId, patch) => {
          await onUpdateEntry?.(entryId, patch);
          setEditModal(null);
        }}
        onDelete={async (entryId) => {
          await onDeleteEntry?.(entryId);
          setEditModal(null);
        }}
      />

      <style>{weekCalCss}</style>
    </div>
  );
};

// ─── Create modal ─────────────────────────────────────────────────────────

const CreateEntryModal = ({ show, slot, projects, onHide, onSubmit }) => {
  const [project, setProject] = useState(null);
  const [category, setCategory] = useState(CATEGORY_OPTIONS[0]);
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !slot) return;
    setProject(null);
    setCategory(CATEGORY_OPTIONS[0]);
    setStartInput(fmtClock(slot.startMins));
    setEndInput(fmtClock(slot.startMins + slot.durationMins));
    setDescription('');
    setError(null);
  }, [show, slot]);

  if (!show || !slot) return null;

  const parseClock = (str) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(str.trim());
    if (!m) return null;
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h < 0 || h > 23 || min < 0 || min > 59) return null;
    return h * 60 + min;
  };

  const handleSave = async () => {
    if (!project) { setError('Välj projekt'); return; }
    const startMins = parseClock(startInput);
    const endMins = parseClock(endInput);
    if (startMins === null || endMins === null) { setError('Använd HH:MM, t.ex. 09:00'); return; }
    if (endMins <= startMins) { setError('Slut måste vara efter start'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        entryDate: format(slot.day, 'yyyy-MM-dd'),
        startTime: buildLocalDatetime(slot.day, startMins),
        endTime: buildLocalDatetime(slot.day, endMins),
        durationMinutes: endMins - startMins,
        kind: 'work',
        workCategory: category.value,
        projectId: project.value,
        description: description || null,
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Kunde inte skapa post');
    } finally {
      setSaving(false);
    }
  };

  const projectOptions = projects.map(p => ({
    value: p.project_id,
    label: `${p.code ? p.code + ' — ' : ''}${p.name}`,
    color: p.color,
  }));

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={16} style={{ color: 'var(--primary)' }} />
          Ny tidspost · {format(slot.day, 'EEEE d MMM')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        <div style={{ marginBottom: 14 }}>
          <label className="tr-cal-modal-label">Projekt <span style={{ color: S.colors.danger }}>*</span></label>
          <Select
            options={projectOptions}
            value={project}
            onChange={setProject}
            placeholder="Välj projekt..."
            isSearchable
            autoFocus
            theme={brandSelectTheme}
            styles={{ control: b => ({ ...b, minHeight: 38, fontSize: 13 }) }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label className="tr-cal-modal-label">Start</label>
            <input type="text" value={startInput} onChange={e => setStartInput(e.target.value)} className="tr-cal-modal-input" placeholder="09:00" />
          </div>
          <div style={{ flex: 1 }}>
            <label className="tr-cal-modal-label">Slut</label>
            <input type="text" value={endInput} onChange={e => setEndInput(e.target.value)} className="tr-cal-modal-input" placeholder="10:30" />
          </div>
          <div style={{ flex: 1 }}>
            <label className="tr-cal-modal-label">Typ</label>
            <Select
              options={CATEGORY_OPTIONS}
              value={category}
              onChange={setCategory}
              isSearchable={false}
              theme={brandSelectTheme}
              styles={{ control: b => ({ ...b, minHeight: 38, fontSize: 13 }) }}
            />
          </div>
        </div>

        <div>
          <label className="tr-cal-modal-label">Beskrivning (valfritt)</label>
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="tr-cal-modal-input" />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Avbryt</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !project}>
          {saving ? 'Sparar…' : 'Skapa'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Edit modal ───────────────────────────────────────────────────────────

const EditEntryModal = ({ show, entry, projects, leaveTypes, onHide, onSubmit, onDelete }) => {
  const [project, setProject] = useState(null);
  const [category, setCategory] = useState(CATEGORY_OPTIONS[0]);
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !entry) return;
    const proj = projects.find(p => p.project_id === entry.project_id);
    setProject(proj ? { value: proj.project_id, label: `${proj.code ? proj.code + ' — ' : ''}${proj.name}`, color: proj.color } : null);
    setCategory(CATEGORY_OPTIONS.find(c => c.value === entry.work_category) || CATEGORY_OPTIONS[0]);
    const startMins = minutesFromMidnight(entry.start_time);
    const durMins = entry.duration_minutes || 0;
    setStartInput(startMins !== null ? fmtClock(startMins) : '');
    setEndInput(startMins !== null ? fmtClock(startMins + durMins) : '');
    setDescription(entry.description || '');
    setError(null);
  }, [show, entry, projects]);

  if (!show || !entry) return null;

  const isAbsence = entry.kind === 'absence';

  const parseClock = (str) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(str.trim());
    if (!m) return null;
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h < 0 || h > 23 || min < 0 || min > 59) return null;
    return h * 60 + min;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const patch = {};
      if (!isAbsence) {
        if (project && project.value !== entry.project_id) patch.projectId = project.value;
        if (category.value !== entry.work_category) patch.workCategory = category.value;
        const startMins = parseClock(startInput);
        const endMins = parseClock(endInput);
        if (startMins === null || endMins === null) { setError('Använd HH:MM, t.ex. 09:00'); setSaving(false); return; }
        if (endMins <= startMins) { setError('Slut måste vara efter start'); setSaving(false); return; }
        const day = new Date(entry.entry_date);
        patch.startTime = buildLocalDatetime(day, startMins);
        patch.endTime = buildLocalDatetime(day, endMins);
        patch.durationMinutes = endMins - startMins;
      }
      if ((description || '') !== (entry.description || '')) patch.description = description || null;

      if (Object.keys(patch).length === 0) { onHide(); return; }
      await onSubmit(entry.entry_id, patch);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Kunde inte spara');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm('Ta bort denna tidspost?')) return;
    setSaving(true);
    try {
      await onDelete(entry.entry_id);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ta bort');
      setSaving(false);
    }
  };

  const projectOptions = projects.map(p => ({
    value: p.project_id,
    label: `${p.code ? p.code + ' — ' : ''}${p.name}`,
    color: p.color,
  }));

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={16} style={{ color: 'var(--primary)' }} />
          Redigera · {format(new Date(entry.entry_date), 'EEEE d MMM')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        {isAbsence ? (
          <div style={{ padding: '12px 16px', background: S.colors.warningLight, borderRadius: 6, marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: S.colors.warning, marginBottom: 4 }}>
              {entry.leave_type_name || 'Frånvaro'}
            </div>
            <div style={{ fontSize: 12, color: S.colors.textMuted }}>
              {Math.round((entry.duration_minutes || 0) / 60 * 10) / 10}h · Redigera frånvaro i tabell-vyn eller ta bort här.
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 14 }}>
              <label className="tr-cal-modal-label">Projekt</label>
              <Select
                options={projectOptions}
                value={project}
                onChange={setProject}
                isSearchable
                theme={brandSelectTheme}
                styles={{ control: b => ({ ...b, minHeight: 38, fontSize: 13 }) }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label className="tr-cal-modal-label">Start</label>
                <input type="text" value={startInput} onChange={e => setStartInput(e.target.value)} className="tr-cal-modal-input" />
              </div>
              <div style={{ flex: 1 }}>
                <label className="tr-cal-modal-label">Slut</label>
                <input type="text" value={endInput} onChange={e => setEndInput(e.target.value)} className="tr-cal-modal-input" />
              </div>
              <div style={{ flex: 1 }}>
                <label className="tr-cal-modal-label">Typ</label>
                <Select
                  options={CATEGORY_OPTIONS}
                  value={category}
                  onChange={setCategory}
                  isSearchable={false}
                  theme={brandSelectTheme}
                  styles={{ control: b => ({ ...b, minHeight: 38, fontSize: 13 }) }}
                />
              </div>
            </div>
          </>
        )}

        <div>
          <label className="tr-cal-modal-label">Beskrivning</label>
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="tr-cal-modal-input" />
        </div>
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: 'space-between' }}>
        <Button variant="outline-danger" onClick={handleDelete} disabled={saving}>
          <Trash2 size={14} className="me-1" /> Ta bort
        </Button>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Avbryt</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Sparar…' : 'Spara'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// ─── CSS ──────────────────────────────────────────────────────────────────

const GUTTER_WIDTH = 56;

const weekCalCss = `
  .tr-cal { background: white; }
  .tr-cal-inner { user-select: none; }

  .tr-cal-header {
    display: grid;
    border-bottom: 1px solid ${S.colors.borderLight};
  }
  .tr-cal-header-cell {
    padding: 10px 6px;
    border-left: 1px solid ${S.colors.borderLight};
    display: flex; flex-direction: row; align-items: center;
    justify-content: center; gap: 10px;
  }
  .tr-cal-header-cell.today { background: var(--rgba-primary-1); }
  .tr-cal-header-cell.weekend { background: rgba(239,68,68,0.03); }
  .tr-cal-date {
    font-size: 26px; font-weight: 600; line-height: 1;
    color: ${S.colors.textPrimary};
    font-variant-numeric: tabular-nums;
  }
  .tr-cal-date.today-date {
    color: var(--primary); font-weight: 700;
  }
  .tr-cal-meta {
    display: flex; flex-direction: column; gap: 3px;
    min-width: 0; text-align: left;
  }
  .tr-cal-wd {
    font-size: 11px; font-weight: 500; color: ${S.colors.textLight};
    text-transform: uppercase; letter-spacing: 0.04em;
    line-height: 1;
  }
  .tr-cal-header-cell.today .tr-cal-wd { color: var(--primary); }
  .tr-cal-day-total {
    font-size: 12px; font-weight: 500;
    color: ${S.colors.textMuted};
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }
  .tr-cal-day-total.zero { color: ${S.colors.textDisabled}; }
  .tr-cal-day-total.over { color: ${S.colors.danger}; font-weight: 600; }
  .tr-cal-header-cell.today .tr-cal-day-total { color: var(--primary); }

  .tr-cal-allday {
    display: grid;
    border-bottom: 2px solid ${S.colors.borderDefault};
    background: ${S.colors.bgSubtle};
    min-height: 34px;
  }
  .tr-cal-allday-label {
    font-size: 10px; color: ${S.colors.textLight};
    text-transform: uppercase; letter-spacing: 0.04em;
    display: flex; align-items: center; justify-content: flex-end;
    padding-right: 8px;
  }
  .tr-cal-allday-cell {
    border-left: 1px solid ${S.colors.borderLight};
    padding: 4px;
    display: flex; flex-direction: column; gap: 3px;
  }
  .tr-cal-allday-cell.today { background: var(--rgba-primary-1); }
  .tr-cal-allday-cell.weekend { background: rgba(239,68,68,0.03); }
  .tr-cal-chip {
    display: flex; align-items: center; gap: 6px;
    padding: 3px 6px; border-radius: 4px;
    font-size: 11px; font-weight: 500;
    cursor: pointer;
    overflow: hidden;
  }
  .tr-cal-chip-hours { font-weight: 700; font-variant-numeric: tabular-nums; flex-shrink: 0; }
  .tr-cal-chip-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .tr-cal-body {
    display: grid;
    grid-template-columns: ${GUTTER_WIDTH}px 1fr;
    position: relative;
  }
  .tr-cal-time-gutter {
    position: relative;
    border-right: 1px solid ${S.colors.borderDefault};
  }
  .tr-cal-hour-label {
    position: absolute;
    right: 8px;
    font-size: 10px; color: ${S.colors.textLight};
    font-variant-numeric: tabular-nums;
    transform: translateY(-6px);
  }

  .tr-cal-cols {
    display: grid;
    position: relative;
  }
  .tr-cal-col {
    position: relative;
    border-left: 1px solid ${S.colors.borderDefault};
    cursor: copy;
  }
  .tr-cal-col.readonly { cursor: default; }
  .tr-cal-col.today { background: var(--rgba-primary-1); }
  .tr-cal-col.weekend { background: rgba(239,68,68,0.03); }
  .tr-cal-hour-line {
    position: absolute; left: 0; right: 0;
    border-top: 1px solid ${S.colors.borderDefault};
    pointer-events: none;
  }
  .tr-cal-hour-line.alt { border-top-style: dashed; opacity: 0.7; }

  .tr-cal-block {
    position: absolute;
    border-radius: 5px;
    padding: 4px 6px;
    font-size: 11px;
    overflow: hidden;
    cursor: grab;
    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
    transition: box-shadow 120ms ease;
    touch-action: none;
  }
  .tr-cal-block:hover {
    box-shadow: 0 2px 6px rgba(0,0,0,0.10);
    z-index: 2;
  }
  .tr-cal-block.dragging {
    opacity: 0.85; cursor: grabbing; z-index: 5;
    box-shadow: 0 4px 10px rgba(0,0,0,0.15);
  }
  .tr-cal-block-head {
    display: flex; align-items: center; justify-content: space-between; gap: 4px;
  }
  .tr-cal-block-time {
    font-weight: 600; font-variant-numeric: tabular-nums; font-size: 10px;
    opacity: 0.85;
  }
  .tr-cal-block-cat {
    font-size: 9px; font-weight: 700; letter-spacing: 0.5px;
    padding: 1px 4px; border-radius: 3px;
  }
  .tr-cal-block-title {
    margin-top: 1px;
    font-weight: 600; font-size: 11px;
    line-height: 1.2;
    overflow: hidden; text-overflow: ellipsis; display: -webkit-box;
    -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  }
  .tr-cal-block-desc {
    margin-top: 2px;
    font-size: 10px; opacity: 0.7;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .tr-cal-resize-handle {
    position: absolute; left: 0; right: 0; bottom: 0;
    height: 6px; cursor: ns-resize;
    background: transparent;
  }
  .tr-cal-resize-handle:hover { background: rgba(0,0,0,0.08); }

  .tr-cal-creating {
    position: absolute; left: 2px; right: 2px;
    border-radius: 5px;
    border: 2px dashed var(--primary);
    background: var(--rgba-primary-1);
    pointer-events: none;
    z-index: 3;
    display: flex; align-items: flex-start; justify-content: flex-start;
    padding: 4px 6px;
  }
  .tr-cal-creating-label {
    font-size: 10px; font-weight: 700; color: var(--primary);
    font-variant-numeric: tabular-nums;
  }

  .tr-cal-now {
    position: absolute; left: 0; right: 0;
    height: 2px; background: #dc2626; z-index: 4;
    pointer-events: none;
  }
  .tr-cal-now::before {
    content: ''; position: absolute; left: -4px; top: -3px;
    width: 8px; height: 8px; border-radius: 50%; background: #dc2626;
  }

  .tr-cal-modal-label {
    display: block; margin-bottom: 4px;
    font-size: 11px; font-weight: 600; color: ${S.colors.textLight};
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .tr-cal-modal-input {
    width: 100%; height: 38px; padding: 0 12px;
    border: 1px solid ${S.colors.borderDefault}; border-radius: 6px;
    font-size: 13px; outline: none;
    transition: border-color 150ms, box-shadow 150ms;
    font-family: inherit;
  }
  .tr-cal-modal-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(68,129,78,0.12); }
`;

export default WeekCalendarGrid;
